import React, { useEffect, useState } from 'react'
import './CreateContactModal.scoped.sass'
import modalService from '@/services/modal.service'
import contactsService from '@/app-domains/contacts/contacts.service'
import store from '@/core/store'

import { Button } from '@/components/Button/Button'
import { InputMask } from '@/components'
import { ReactComponent as CloseIcon } from '@/assets/icons/close-icon.svg'
import { ReactComponent as PersonIcon } from '@/assets/icons/person-icon.svg'
import { ReactComponent as PhoneIcon } from '@/assets/icons/phone-icon.svg'
import { ReactComponent as LetterIcon } from '@/assets/icons/letter-icon.svg'
import { ReactComponent as WebsiteIcon } from '@/assets/icons/website-icon.svg'
import { ReactComponent as AddressIcon } from '@/assets/icons/address-icon.svg'
import { ReactComponent as NotesIcon } from '@/assets/icons/notes-icon.svg'
// eslint-disable-next-line
import { ReactComponent as ImageAvatar } from '@/assets/icons/avatar-image.svg'
import { ReactComponent as LabelsIcon } from '@/assets/icons/labels-icon.svg'
import { ReactComponent as CameraIcon } from '@/assets/icons/camera-icon.svg'
import isEmail from 'validator/lib/isEmail'
import ToastService from '@/components/Toast/Toast.service'
import { ManageLabelsForContactFloatingMenu } from './ManageLabelsForContactFloatingMenu/ManageLabelsForContactFloatingMenu'
import contactslabelsService from '@/app-domains/contactslabels/contactslabels.service'

const CreateContactModal = (props) => {
  const { contact, type, dataToFillInCreationForm, fromThread } = props
  const editableContact = contact ? JSON.parse(JSON.stringify(contact)) : { emails: [], phones: [], addresses: [] }
  const [currentContact, setCurrentContact] = useState(editableContact)
  const [submitting, setSubmitting] = useState(false)
  const [selectedLabels, setSelectedLabels] = useState(contact ? contactslabelsService.findLabelsForContact(contact) : [])
  const [isLabelsSectionOpened, setIsLabelSectionOpened] = useState(false)
  const [isProfileImageEdited, setIsProfileImageEdited] = useState(false)

  function fillContactDataIfNotEditableWithDataToFillInCreationForm (dataToFillInCreationForm) {
    setCurrentContact({
      ...dataToFillInCreationForm
    })
  }
  function handleFirstnameChange (value) {
    setCurrentContact({
      ...currentContact,
      firstname: value
    })
  }

  function handleLastnameChange (value) {
    setCurrentContact({
      ...currentContact,
      lastname: value
    })
  }

  function handleEmailChange (value, index) {
    currentContact.emails[index] = value
    setCurrentContact({
      ...currentContact,
    })
  }

  function handlePhoneChange (value, index) {
    currentContact.phones[index] = value
    setCurrentContact({
      ...currentContact,
    })
  }

  function handleAddressChange (value, index) {
    currentContact.addresses[index] = value
    setCurrentContact({
      ...currentContact,
    })
  }
  function handleWebsiteChange (value) {
    setCurrentContact({
      ...currentContact,
      website: value
    })
  }
  function handleNotesChange (value) {
    setCurrentContact({
      ...currentContact,
      notes: value,
    })
  }

  function addPhoneNumberInput () {
    setCurrentContact({
      ...currentContact,
      phones: [...currentContact.phones, '']
    })
  }

  function addEmailAddressInput () {
    setCurrentContact({
      ...currentContact,
      emails: [...currentContact.emails, '']
    })
  }

  function addAddressInput () {
    setCurrentContact({
      ...currentContact,
      addresses: [...currentContact.addresses, '']
    })
  }

  function getModalTitle () {
    if (type === 'new') {
      return 'Create a new contact'
    } else if (type === 'edit') {
      return 'Edit an existing contact'
    }
  }

  function isValidContactData (contact) {
    // Filter empty emails
    contact.emails = contact.emails?.filter(e => e)

    // Check for duplicates in email
    const uniqueMails = Array.from(new Set(contact.emails))
    if (uniqueMails?.length !== contact?.emails?.length) {
      ToastService.show({
        type: 'error',
        title: 'Validation error',
        message: 'There are repeated mails'
      })
      return false
    }

    const uniquePhones = Array.from(new Set(contact.phones))
    if (contact.phones && uniquePhones?.length !== contact?.phones?.length) {
      ToastService.show({
        type: 'error',
        title: 'Validation error',
        message: 'There are repeated phones'
      })
      return false
    }

    // Check for invalid emails
    for (const email of contact.emails) {
      if (!isEmail(email)) {
        ToastService.show({
          type: 'error',
          title: 'Validation error',
          message: 'There are invalid mails'
        })
        return false
      }
    }

    return true
  }

  function getSubmitButton () {
    if (type === 'new') {
      return (
        <Button disabled={submitting} content='Create Contact' click={async () => {
          if (!isValidContactData(currentContact)) { return }
          setSubmitting(true)
          const newContact = await contactsService.addContact(currentContact)
          store.commit('CONTACT_LIST:UPDATED', Object.values(contactsService.getAllStoredContacts()))
          store.commit('CONTACT_VIEW:UPDATED', newContact)
          setSubmitting(false)
          fromThread && store.commit(`THREAD:${fromThread.composedid}:RENDER`)
          modalService.close()
        }}/>
      )
    } else if (type === 'edit') {
      return (
        <Button disabled={submitting} content='Edit Contact' click={async () => {
          if (!isValidContactData(currentContact)) { return }
          setSubmitting(true)
          await contactsService.editContact(currentContact)
          store.commit('CONTACT_VIEW:UPDATED', currentContact)
          store.commit(`CONTACT:${contact.id}:UPDATED`, currentContact)
          setSubmitting(false)
          modalService.close()
        }}/>
      )
    }
  }

  async function onChangeProfileImage (base64Image, file) {
    // Validate image size here
    const data = {
      filename: file.name,
      file: base64Image.split(',')[1],
      contentType: file.type,
      originalData: base64Image,
    }
    // eslint-disable-next-line
    
    setCurrentContact({
      ...currentContact,
      profileImageData: data
    })
    setIsProfileImageEdited(true)
  }

  function handlePictureInput () {
    const inputEl = document.createElement('input')
    inputEl.setAttribute('type', 'file')
    inputEl.addEventListener('change', function () {
      const files = inputEl.files
      if (files.length) {
        // eslint-disable-next-line no-undef
        const reader = new FileReader()
        reader.readAsDataURL(files[0])
        reader.onload = () => onChangeProfileImage(reader.result, files[0])
      }
    }, false)
    inputEl.click()
  }

  function onLabelSelected (newlySelectedLabel) {
    if (selectedLabels.find((label) => label.composedid === newlySelectedLabel.composedid)) {
      const newSelectedLabels = selectedLabels.filter((label) => label.composedid !== newlySelectedLabel.composedid)
      setCurrentContact({
        ...currentContact,
        selectedLabels: newSelectedLabels
      })
      setSelectedLabels(newSelectedLabels)
    } else {
      setCurrentContact({
        ...currentContact,
        selectedLabels: [newlySelectedLabel, ...selectedLabels]
      })
      setSelectedLabels([newlySelectedLabel, ...selectedLabels])
    }
  }

  useEffect(() => {
    if (!contact && dataToFillInCreationForm) {
      fillContactDataIfNotEditableWithDataToFillInCreationForm(dataToFillInCreationForm)
    }
  }, [])

  return (
    <div className='main-container'>
      <div className='top-header'>
        <span className='create-contact-header-text'>
          {getModalTitle()}
        </span>
        <div className='close-button' onClick={() => { modalService.close() }}>
          <CloseIcon />
        </div>
      </div>
      <div className='picture-section-div' onMouseDown={handlePictureInput}>
        { (contact?.contactimageuri || isProfileImageEdited)
          ? (isProfileImageEdited
              ? <img className='contact-image-img' src={currentContact.profileImageData.originalData}/>
              : <img className='contact-image-img' src={contact.contactimageuri}/>
            )
          : <div className='picture-div'>
            <div className='picture-input'>
              <div className='camera-logo'>
                <CameraIcon />
              </div>
              <span className='main-text'>
                Select a picture
              </span>
              <span className='secondary-text'>
                or drag one here
              </span>
            </div>
          </div>
        }
      </div>
      
      <div className='contact-info'>
        <div className='fullname-div'>
          <div className='fullname-left container-left'>
            <div className='icon'>
              <PersonIcon />
            </div>
            <span className='fullname-text'>
              Full name
            </span>
          </div>
          <div className='fullname-input'>
            <InputMask value={currentContact.firstname} placeholder='First Name ...' size='small' isBorderDisable={true} hasLabel={false} onInput={(value) => handleFirstnameChange(value)}/>
            <InputMask value={currentContact.lastname} placeholder='Last Name ...' size='small' isBorderDisable={true} hasLabel={false} onInput={(value) => handleLastnameChange(value)}/>
          </div>
        </div>
        <div className='info-div'>
          <div className='info-left container-left'>
            <div className='icon'>
              <PhoneIcon />
            </div>
            <span className='info-text'>
              Phone Number
            </span>
          </div>
          <div className='info-input'>
            {currentContact.phones && currentContact.phones.map((phone, index) => {
              return (<div key={index} className="input-field">
                <InputMask key={`phone-${index}`} value={phone} placeholder='Phone Number ...' hasLabel={false} isBorderDisable={true} onInput={(value) => handlePhoneChange(value, index)}/>
              </div>)
            })}
            <div className='add-info-button phone' onClick={addPhoneNumberInput}>
              Add phone number +
            </div>
          </div>
        </div>
        <div className='info-div'>
          <div className='info-left container-left'>
            <div className='icon'>
              <LetterIcon />
            </div>
            <span className='info-text'>
              Email
            </span>
          </div>
          <div className='info-input'>
            {currentContact.emails && currentContact.emails.map((email, index) => {
              return (<div key={index} className="input-field">
                <InputMask key={`email-${index}`} value={email} placeholder='Email ...' currentContact={currentContact} setCurrentContact={setCurrentContact} isBorderDisable={true} hasLabel={false} onInput={(value) => handleEmailChange(value, index)}/>
              </div>)
            })}
            <div className='add-info-button email' onClick={addEmailAddressInput}>
              Add email address +
            </div>
          </div>
        </div>
        <div className='info-div'>
          <div className='info-left container-left'>
            <div className='icon'>
              <WebsiteIcon />
            </div>
            <span className='info-text'>
              Website
            </span>
          </div>
          <div className='info-input'>
            <InputMask placeholder='Website ...' value={currentContact.website} isBorderDisable={true} hasLabel={false} onInput={(value) => handleWebsiteChange(value)}/>
          </div>
        </div>
        <div className='info-div'>
          <div className='info-left container-left'>
            <div className='icon'>
              <AddressIcon />
            </div>
            <span className='info-text'>
              Address
            </span>
          </div>
          <div className='info-input'>
            {currentContact.addresses && currentContact.addresses.map((address, index) => {
              return (<div key={index} className="input-field">
                <InputMask key={`address-${index}`} placeholder='Address ...' value={address} isBorderDisable={true} hasLabel={false} onInput={(value) => handleAddressChange(value, index)}/>
              </div>)
            })}
            <div className='add-info-button address' onClick={addAddressInput}>
              Add address +
            </div>
          </div>
        </div>
        <div className='info-div'>
          <div className='info-left container-left'>
            <div className='icon'>
              <NotesIcon />
            </div>
            <span className='info-text'>
              Notes
            </span>
          </div>
          <div className='info-input'>
            <InputMask placeholder='Notes ...' isBorderDisable={true} hasLabel={false} onInput={(value) => handleNotesChange(value)}/>
          </div>
        </div>
        <div className='labels-div'>
          <div className='labels-title container-left'>
            <div className='icon'>
              <LabelsIcon />
            </div>
            <span className='labels-text'>
              Labels
            </span>
          </div>
          <div className='labels-input'>
            {contact?.contactlabels.map((label, index) => <div key={index} className='contact-label'>
              <div className='label-badge'>
                {label.name}
              </div>
            </div>)
            }
            {currentContact.selectedLabels?.map((label, index) => <div key={index} className='contact-label'>
              <div className='label-badge'>
                {label.name}
              </div>
            </div>)
            }
            <div className='label-badge add' onClick={() => setIsLabelSectionOpened(true)}>
              +
            </div>
            {isLabelsSectionOpened && <ManageLabelsForContactFloatingMenu
              onBlur={setIsLabelSectionOpened}
              labels={Object.values(contactslabelsService.getAllStoredContactLabels())}
              onLabelSelected={onLabelSelected}
              selectedLabels={currentContact.selectedLabels}
              />}
          </div>
        </div>
      
      </div>
      <div className='buttons-div'>
        <div className='cancel-button'>
          <Button content='Cancel' type='secondary' click={() => { modalService.close() }}/>
        </div>
        <div className='create-contact-button'>
          {getSubmitButton()}
        </div>
      </div>
    </div>
  )
}

export default CreateContactModal
